import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Zap, CheckCircle2, CalendarClock } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

/**
 * Shared body for the Service Notice (used by both the section card and the modal).
 * `compact` tightens spacing for the modal context.
 */
export const NoticeBody = ({ content, lang, setLang, compact = false }) => {
  const c = content[lang];
  return (
    <>
      {/* Header */}
      <div className={`flex flex-col gap-4 border-b border-[color:var(--border-subtle)] bg-[var(--surface-2)] ${compact ? "px-5 py-4 sm:px-6" : "px-6 py-5 sm:px-8"} sm:flex-row sm:items-center sm:justify-between`}>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[rgba(139,30,63,0.08)] text-[var(--brand-burgundy)]">
            <Clock className="h-6 w-6" strokeWidth={1.75} />
          </span>
          <div>
            <span className="inline-block rounded-full bg-[var(--brand-burgundy)] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              {c.badge}
            </span>
            <h2 className="mt-1 font-heading text-xl font-bold leading-tight text-[var(--brand-navy)] sm:text-2xl">
              {c.title}
            </h2>
          </div>
        </div>

        <div
          className="inline-flex shrink-0 self-start rounded-full border border-[color:var(--border-subtle)] bg-white p-0.5 sm:self-center"
          role="group"
          aria-label="Notice language"
          data-testid="notice-language-toggle"
        >
          {["en", "es"].map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              aria-pressed={lang === l}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                lang === l
                  ? "bg-[var(--brand-burgundy)] text-white"
                  : "text-[var(--muted-ink)] hover:text-[var(--brand-charcoal)]"
              }`}
              data-testid={`notice-lang-${l}`}
            >
              {l === "en" ? "English" : "Español"}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <AnimatePresence mode="wait">
        <motion.div
          key={lang}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.25, ease }}
          className={compact ? "px-5 py-6 sm:px-6" : "px-6 py-7 sm:px-8 sm:py-8"}
        >
          <p className="text-sm leading-relaxed text-[var(--muted-ink)] sm:text-[15px]">{c.intro}</p>

          <div className="mt-6 flex items-center gap-3 rounded-2xl border border-[color:var(--border-subtle)] bg-[var(--surface-2)] px-5 py-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-[var(--brand-burgundy)] shadow-[var(--shadow-sm)]">
              <CalendarClock className="h-6 w-6" strokeWidth={1.75} />
            </span>
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-[var(--muted-ink)]">{c.standardLabel}</div>
              <div className="font-heading text-xl font-bold text-[var(--brand-navy)]">{c.standardValue}</div>
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[rgba(139,30,63,0.08)] text-[var(--brand-burgundy)]">
                <Zap className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <h3 className="font-heading text-lg font-semibold text-[var(--brand-navy)] sm:text-xl">{c.subtitle}</h3>
            </div>
            <ul className="mt-4 space-y-3">
              {c.bullets.map((b) => (
                <li key={b.slice(0, 40)} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[var(--brand-burgundy)]" strokeWidth={1.75} />
                  <span className="text-sm leading-relaxed text-[var(--brand-charcoal)] sm:text-[15px]">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex items-center gap-2.5 border-t border-[color:var(--border-subtle)] pt-5">
            <Clock className="h-4 w-4 shrink-0 text-[var(--muted-ink)]" />
            <p className="font-heading text-base font-semibold italic text-[var(--brand-navy)] sm:text-lg">{c.footer}</p>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default NoticeBody;
